import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { TableColumn } from '@/components/common/DataTable/types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import {
  rolePermissionsService,
  type RolePermissionMapping,
  type CreateRolePermissionPayload,
} from '@/superadmin/services/rolePermissionsService';
import { rolesService, type RoleApiModel } from '@/superadmin/services/rolesService';
import {
  permissionsService,
  type PermissionApiModel,
} from '@/superadmin/services/permissionsService';
import { RolePermissionMappingFormDrawer } from '../../components';
import { toast } from 'react-hot-toast';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

const RolePermissionsPage: React.FC = () => {
  const [rolePermissionMappings, setRolePermissionMappings] = useState<RolePermissionMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [roles, setRoles] = useState<RoleApiModel[]>([]);
  const [permissions, setPermissions] = useState<PermissionApiModel[]>([]);
  const [fetchingFormData, setFetchingFormData] = useState(false);
  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  useEffect(() => {
    fetchRolePermissionMappings();
  }, [currentPage, pageSize]);

  const fetchRolePermissionMappings = async () => {
    try {
      setLoading(true);
      const response = await rolePermissionsService.getRolePermissionMappings(
        currentPage,
        pageSize
      );
      console.log('Role Permission Mappings API Response:', response);

      setRolePermissionMappings(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Failed to load role permission mappings:', error);
      toast.error('Failed to load role permission mappings');
      setRolePermissionMappings([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesAndPermissions = async () => {
    try {
      setFetchingFormData(true);

      // Fetch both roles and permissions in parallel
      const [rolesResponse, permissionsResponse] = await Promise.all([
        rolesService.list(1, 100), // Fetch all roles (100 should be enough)
        permissionsService.list(1, 100), // Fetch all permissions (100 should be enough)
      ]);

      setRoles(rolesResponse.results || []);
      setPermissions(permissionsResponse.results || []);
    } catch (error) {
      console.error('Failed to fetch roles and permissions:', error);
      toast.error('Failed to load roles and permissions');
      setRoles([]);
      setPermissions([]);
    } finally {
      setFetchingFormData(false);
    }
  };

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/role-permissions');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
          { label: 'Dashboard', href: '/superadmin/dashboard' },
          { label: 'Role Permissions', active: true },
        ];

  // Role Permission Mappings table columns
  const columns: TableColumn<RolePermissionMapping>[] = [
    {
      field: 'role_name',
      headerName: 'Role Name',
      minWidth: 200,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" fontWeight="medium">
          {String(params.value)}
        </Typography>
      ),
    },
    {
      field: 'permission_name',
      headerName: 'Permission Name',
      minWidth: 250,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" fontWeight="medium">
          {String(params.value)}
        </Typography>
      ),
    },
    {
      field: 'permission_description',
      headerName: 'Description',
      minWidth: 300,
      sortable: false,
      renderCell: params => (
        <Typography variant="body2" color="textSecondary">
          {String(params.value)}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      minWidth: 120,
      sortable: true,
      renderCell: params => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      minWidth: 180,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2">{new Date(String(params.value)).toLocaleString()}</Typography>
      ),
    },
  ];

  // Table actions
  const actions = [
  hasPermission('ADMIN_ROLE_PERMISSION_DELETE') && {
    label: 'Delete',
    icon: <DeleteIcon />,
    onClick: (row: RolePermissionMapping) => {
      // TODO: Implement delete functionality
      console.log('Delete role permission mapping:', row);
      toast('Delete functionality not implemented yet');
    },
    color: 'error' as const,
  },
].filter(Boolean);

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleCreateMapping = async (payload: CreateRolePermissionPayload) => {
    try {
      setFormLoading(true);
      const response = await rolePermissionsService.createRolePermission(payload);

      if (response.success) {
        toast.success(response.message || 'Role permission mapping created successfully');
        setDrawerOpen(false);
        // Refresh the data
        fetchRolePermissionMappings();
      } else {
        toast.error(response.message || 'Failed to create role permission mapping');
      }
    } catch (error) {
      console.warn('Failed to create role permission mapping:', error);
      toast.error('Failed to create role permission mapping');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle opening the drawer and fetching form data
  const handleOpenDrawer = () => {
    setDrawerOpen(true);
    fetchRolesAndPermissions();
  };

  // Header actions
  const headerActions: PageHeaderAction[] = [
    hasPermission('ADMIN_ROLE_PERMISSION_MAPPING') && {
      label: 'Map Role Permission',
      icon: <AddIcon sx={{ fontSize: 16 }} />,
      onClick: handleOpenDrawer,
      variant: 'contained',
      size: 'medium',
    },
  ].filter(Boolean) as PageHeaderAction[];

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      <PageHeader
        title="Role Permissions"
        subtitle={`Manage role permission mappings (${totalCount} ${totalCount === 1 ? 'mapping' : 'mappings'})`}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      {hasPermission('ADMIN_ROLE_PERMISSION_LIST_VIEW') ? (
        <DataTable
          data={rolePermissionMappings as unknown as Record<string, unknown>[]}
          columns={columns as unknown as TableColumn<Record<string, unknown>>[]}
          actions={actions as any}
          keyField="uuid"
          title="Role Permission Mappings"
          showTitle={false}
          loading={loading}
          pagination={true}
          rowsPerPageOptions={[5, 10, 15, 25, 50]}
          defaultRowsPerPage={15}
          page={currentPage - 1}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={page => handlePageChange(page + 1)}
          onRowsPerPageChange={handlePageSizeChange}
          sortable={true}
          stickyHeader={true}
          maxHeight={600}
          striped={true}
          emptyState={
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No role permission mappings found.
              </Typography>
            </Box>
          }
        />
      ) : (
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view.
        </Typography>
      )}

      <RolePermissionMappingFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleCreateMapping}
        roles={roles as any}
        permissions={permissions as any}
        loading={formLoading || fetchingFormData}
      />
    </Box>
  );
};

export default RolePermissionsPage;
